/**
 * useCreateFirstWorkspace Hook
 *
 * Shared logic for creating a user's first workspace.
 * Used by both the setup-pending page (webhook timeout fallback)
 * and the no-access page (zero workspaces state).
 *
 * Also exports helpers for workspace-ready checks and cache management.
 */

import { useState, useCallback, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { useUser } from '@clerk/clerk-react';

import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';
import { useWorkspaceStore } from '@/store/useWorkspaceStore';
import { toast } from '@/lib/toast';

export const DEFAULT_WORKSPACE_NAME = 'My Workspace';

/**
 * Remove stale workspace cache so the resolver fetches fresh data
 * when _withWorkspace mounts. invalidateQueries wouldn't trigger
 * a refetch because the query may have no observers.
 */
export function clearWorkspacesCache(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: queryKeys.workspaces.all });
}

/**
 * Clear stale workspace cache and navigate to the workspace page.
 * Shared by workspace creation success, race-condition recovery,
 * and webhook-ready detection.
 */
export function navigateToWorkspace(queryClient: QueryClient, navigate: ReturnType<typeof useNavigate>) {
  clearWorkspacesCache(queryClient);
  void navigate({ to: '/workspace', replace: true });
}

/**
 * Check whether the Clerk webhook has finished creating the user's workspace.
 */
export function isWorkspaceReady(user: ReturnType<typeof useUser>['user']): boolean {
  return (user?.publicMetadata as Record<string, unknown>)?.workspaceReady === true;
}

interface UseCreateFirstWorkspaceOptions {
  onError?: (message: string) => void;
}

/**
 * Hook that encapsulates workspace creation via the `workspaces.first` endpoint.
 *
 * On success: sets workspace ID, shows toast, clears cache, navigates to /workspace.
 * On 400 "already has a workspace": clears cache, navigates to /workspace.
 * On other errors: calls the optional onError callback.
 */
export function useCreateFirstWorkspace(options?: UseCreateFirstWorkspaceOptions) {
  const rpc = useRpc();
  const { setSelectedWorkspaceId } = useWorkspaceStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  // Ref to avoid including the options object in the useCallback deps array,
  // which would cause re-creation on every render since callers pass inline objects.
  const onErrorRef = useRef(options?.onError);
  onErrorRef.current = options?.onError;

  const createWorkspace = useCallback(async () => {
    setIsCreating(true);
    try {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.workspaces.first.$post({
        json: { name: DEFAULT_WORKSPACE_NAME },
      });

      if (res.ok) {
        const data = (await res.json()) as { workspace: { id: string; name: string } };
        setSelectedWorkspaceId(data.workspace.id);
        toast.access.workspaceCreated(data.workspace.name);
        navigateToWorkspace(queryClient, navigate);
      } else {
        const errorData = (await res.json().catch(() => ({ error: 'Unknown error' }))) as {
          error?: string;
        };

        // If user already has a workspace (race condition), just navigate
        if (res.status === 400 && errorData.error?.includes('already has a workspace')) {
          navigateToWorkspace(queryClient, navigate);
          return;
        }

        onErrorRef.current?.(errorData.error ?? 'Failed to create workspace');
      }
    } catch (error) {
      console.error('Failed to create workspace:', error);
      onErrorRef.current?.('Failed to create workspace. Please try again.');
    } finally {
      setIsCreating(false);
    }
  }, [rpc, setSelectedWorkspaceId, queryClient, navigate]);

  return { createWorkspace, isCreating };
}
